import { CheckSquare, ChevronDown, Loader2, Package, Plus, Send, Square, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { Section, cx } from "./wf";
import { TaskPartsDialog } from "./ProjectTaskDialogs";
import { api, num } from "../lib/api";
import { isManagerUser, useAuth } from "../lib/auth";

const inputCls = "min-h-[48px] flex-1 rounded-xl border border-wf-line bg-wf-s3 px-4 text-[15px] outline-none focus:border-wf-brand focus:bg-wf-surface";
const addBtn = "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-wf-brand text-wf-onbrand disabled:opacity-40";

/** Comments thread + parts list for one punch list item. */
function PunchDetails({ wid, e, onUpdated, onEditParts }) {
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (ev) => {
    ev.preventDefault();
    const text = comment.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch/${e.id}/comments`, { method: "POST", body: { text } }));
      setComment("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };
  const remove = async (cid) => {
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch/${e.id}/comments/${cid}`, { method: "DELETE" }));
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div className="border-t border-wf-line py-3" data-testid={`punch-details-${e.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-extrabold uppercase text-wf-ink2">Parts</span>
        <button data-testid={`punch-edit-parts-${e.id}`} onClick={onEditParts} className="flex items-center gap-1 text-sm font-bold text-wf-brand"><Package size={15} />{e.parts?.length ? "Edit" : "Add"}</button>
      </div>
      <p className="text-xs text-wf-mute" data-testid={`punch-parts-${e.id}`}>{e.parts?.length ? e.parts.map((x) => `${x.name} × ${num(x.quantity)}`).join(" · ") : "No parts yet"}</p>
      <p className="mt-3 text-xs font-extrabold uppercase text-wf-ink2">Comments</p>
      {(e.comments ?? []).map((c) => (
        <div key={c.id} data-testid={`punch-comment-${c.id}`} className="flex items-start gap-2 border-b border-wf-line py-2">
          <div className="min-w-0 flex-1">
            <p className="whitespace-pre-wrap text-sm">{c.text}</p>
            <p className="text-xs text-wf-mute">{c.by} · {new Date(c.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
          </div>
          {(c.by_id === user?.id || isManagerUser(user)) && (
            <button data-testid={`punch-comment-delete-${c.id}`} onClick={() => remove(c.id)} className="rounded-full p-1.5 text-wf-mute hover:text-wf-err"><X size={15} /></button>
          )}
        </div>
      ))}
      <form onSubmit={send} className="mt-2 flex gap-2">
        <textarea data-testid={`punch-comment-input-${e.id}`} value={comment} onChange={(ev) => setComment(ev.target.value)} maxLength={2000} rows={1} placeholder="Add a comment"
          onKeyDown={(ev) => { if (ev.key === "Enter" && !ev.shiftKey) send(ev); }} className={cx(inputCls, "max-h-32 py-3")} />
        <button data-testid={`punch-comment-send-${e.id}`} type="submit" disabled={!comment.trim() || sending} className={addBtn}>
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </form>
    </div>
  );
}

/** Punch list on a job: quick add, click the box to check off, click the item for comments and parts. */
export function PunchList({ wid, items, canEdit, onUpdated, onDelete }) {
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [partsFor, setPartsFor] = useState(null);
  const done = items.filter((e) => e.completed).length;
  const sorted = [...items].sort((a, b) => Number(!!a.completed) - Number(!!b.completed));

  const add = async (e) => {
    e.preventDefault();
    const description = text.trim();
    if (!description || adding) return;
    setAdding(true);
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch`, { method: "POST", body: { description } }));
      setText("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };
  const toggle = async (e) => {
    setBusyId(e.id);
    try {
      onUpdated(await api(`/work-orders/${wid}/entries/punch/${e.id}/complete`, { method: "POST", body: { progress: e.completed ? 0 : 1 } }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="entries-section-punch">
      <Section title={`Punch List (${items.length ? `${done} of ${items.length} done` : 0})`} />
      {sorted.map((e) => {
        const open = openId === e.id;
        const extras = [e.comments?.length && `${e.comments.length} comment${e.comments.length === 1 ? "" : "s"}`, e.parts?.length && `${e.parts.length} part${e.parts.length === 1 ? "" : "s"}`].filter(Boolean);
        const meta = e.completed && e.completed_at ? `Done ${new Date(e.completed_at).toLocaleDateString()}${e.completed_by ? ` by ${e.completed_by}` : ""}` : e.created_by ? `Added by ${e.created_by}` : "";
        return (
          <div key={e.id} data-testid={`punch-item-${e.id}`} className="mb-2 rounded-xl border border-wf-line bg-wf-s2 px-3">
            <div className="flex items-center gap-2 py-2">
              <button data-testid={`punch-check-${e.id}`} onClick={() => toggle(e)} disabled={busyId === e.id} role="checkbox" aria-checked={!!e.completed} className="flex h-10 w-10 shrink-0 items-center justify-center">
                {busyId === e.id ? <Loader2 size={22} className="animate-spin text-green-600" /> : e.completed ? <CheckSquare size={22} className="text-green-600" /> : <Square size={22} className="text-wf-mute" />}
              </button>
              <button data-testid={`punch-open-${e.id}`} onClick={() => setOpenId(open ? null : e.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                <span className="min-w-0 flex-1">
                  <span className={cx("block font-semibold", e.completed && "text-wf-mute line-through")}>{e.description}</span>
                  <span className="block text-[11px] text-wf-mute">{[meta, ...extras].filter(Boolean).join(" · ")}</span>
                </span>
                <ChevronDown size={17} className={cx("shrink-0 text-wf-mute transition-transform", open && "rotate-180")} />
              </button>
              {canEdit(e) && (
                <button data-testid={`punch-delete-${e.id}`} onClick={() => onDelete(e.id)} className="rounded-full p-2 text-wf-mute hover:bg-red-50 hover:text-wf-err"><Trash2 size={17} /></button>
              )}
            </div>
            {open && <PunchDetails wid={wid} e={e} onUpdated={onUpdated} onEditParts={() => setPartsFor(e)} />}
          </div>
        );
      })}
      <form onSubmit={add} className="flex gap-2">
        <input data-testid="punch-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a punch list item" className={inputCls} />
        <button data-testid="punch-add-button" type="submit" disabled={!text.trim() || adding} className={addBtn}>
          {adding ? <Loader2 size={20} className="animate-spin" /> : <Plus size={22} />}
        </button>
      </form>
      <TaskPartsDialog task={partsFor ? { title: partsFor.description, parts: partsFor.parts } : null} heading="Punch Item Parts" note="kept on this punch item only"
        onClose={() => setPartsFor(null)}
        onSave={async (parts) => {
          try {
            onUpdated(await api(`/work-orders/${wid}/entries/punch/${partsFor.id}/parts`, { method: "PUT", body: { parts } }));
            toast.success("Parts saved");
            return true;
          } catch (err) {
            toast.error(err.message);
            return false;
          }
        }} />
    </div>
  );
}
